import { useState, useEffect, useMemo, useCallback, Activity } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import FixedDrawer from '@/general-ui/fixed-drawer/fixed-drawer';
import useFixedDrawer from '@/general-ui/fixed-drawer/use-fixed-drawer';
import { EPanelDirections } from '@/store/slices/layout-grid/types';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import SearchInput from '@ui/search-input/search-input';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingProjectRegistrationQuestions } from '@/store/slices/project/selectors';
import { COLUMN_DEFINITIONS, regFieldId } from './profile-column-definitions';
import Container from '@ui/container/container';
import ProfileColumnsSortableRow from './profile-columns-sortable-row';
import { RegFieldsEnum } from '@/types/registration.types';

const EXCLUDED_REG_QUESTION_IDS = new Set([
  RegFieldsEnum.first_name,
  RegFieldsEnum.last_name,
  RegFieldsEnum.email,
  RegFieldsEnum.job_title,
  RegFieldsEnum.department,
  RegFieldsEnum.company_name,
  RegFieldsEnum.industry,
  RegFieldsEnum.phone_number,
]);

type ProfileColumnsSelectProps = {
  enabledColumns: string[];
  columnOrder: string[];
  onToggle: (columnId: string, enabled: boolean) => void;
  onReorder: (newOrder: string[]) => void;
};

const ProfileColumnsSelect = ({ enabledColumns, columnOrder, onToggle, onReorder }: ProfileColumnsSelectProps) => {
  const { id, setCloseDrawer, open, closing } = useFixedDrawer({ id: 'profile-columns-select' });

  const registrationFields = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // close on click away
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      const drawer = document.getElementById(id);
      if (drawer && !drawer.contains(e.target as Node)) {
        setCloseDrawer();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open, id, setCloseDrawer]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const filteredItems = useMemo(() => {
    const needle = search.toLowerCase();

    const customRegFields =
      registrationFields?.filter((f) => !EXCLUDED_REG_QUESTION_IDS.has(Number(f.registration_question))) ?? [];

    // Build an ordered list of all items (static + reg fields), respecting columnOrder
    type RowItem = { id: string; label: string; isRemovable: boolean };

    const allItems: RowItem[] = [
      ...COLUMN_DEFINITIONS.map((def) => ({ id: def.id, label: def.label, isRemovable: def.isRemovable })),
      ...customRegFields.map((f) => ({
        id: regFieldId(f.registration_question),
        label: f.name ?? '',
        isRemovable: true,
      })),
    ];
    const orderedItems = columnOrder
      .map((id) => allItems.find((item) => item.id === id))
      .filter((item): item is RowItem => !!item)
      .concat(allItems.filter((item) => !columnOrder.includes(item.id)));
    const filteredItems = needle
      ? orderedItems.filter((item) => item.label.toLowerCase().includes(needle))
      : orderedItems;
    return filteredItems;
  }, [columnOrder, registrationFields, search]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = columnOrder.indexOf(String(active.id));
      const newIndex = columnOrder.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder(arrayMove(columnOrder, oldIndex, newIndex));
    },
    [columnOrder, onReorder]
  );

  return (
    <FixedDrawer id={id} direction={EPanelDirections.RTL}>
      <Activity mode={open || closing ? 'visible' : 'hidden'}>
        <Container className="flex fd-column" width="400px" height="100vh">
          <Container className="flex ai-center jc-space-between padding-t-16 padding-r-16 padding-b-8 padding-l-24">
            <Text className="H4">Customize view</Text>
            <Button clear onClick={setCloseDrawer}>
              <Icon name={ICONS.CLOSE} size={16} />
            </Button>
          </Container>
          <Container className="padding-v-8 padding-h-24">
            <SearchInput key={open ? 'open' : 'closed'} onSubmit={setSearch} onClear={() => setSearch('')} />
          </Container>
          <Container className="flex fd-column padding-v-12 padding-r-24 gap-8" style={{ overflowY: 'auto' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={filteredItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                {filteredItems.map((item) => (
                  <ProfileColumnsSortableRow
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    isRemovable={item.isRemovable}
                    enabled={enabledColumns.includes(item.id)}
                    onToggle={onToggle}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Container>
        </Container>
      </Activity>
    </FixedDrawer>
  );
};

export default ProfileColumnsSelect;
